import { Checkbox, Paper, TextField } from '@mui/material';

import { includesLowerCase } from '@gozoki/tools';
import { GridColDef } from '@mui/x-data-grid';
import { useState } from 'react';
import SimpleDataGrid from '../../components/containers/SimpleDataGrid';
import { InventoryProductRow } from '../../utils/hooks/useInventoryProductRows';

interface InventoryGridProps {
    isLoading: boolean;
    productRows: InventoryProductRow[];
    inventoryStatus: 'pending' | 'validated' | 'canceled';
    problematicProducts?: string[];
    showValidationColumn?: boolean;
    validatedProducts?: Set<string>;
    onValidationChange?: (productRef: string, checked: boolean) => void;
}

export const InventoryGrid = (props: InventoryGridProps) => {
    const {
        isLoading,
        productRows,
        inventoryStatus,
        problematicProducts = [],
        showValidationColumn = false,
        validatedProducts = new Set(),
        onValidationChange,
    } = props;
    const [search, setSearch] = useState('');

    const stockColumnsDefs: GridColDef[] = [
        {
            field: 'reference',
            headerName: 'Réf',
            flex: 1,
            editable: false,
        },
        {
            field: 'label',
            headerName: 'Libellé',
            flex: 2,
            editable: false,
        },
        {
            field: 'theoricalCountBeforeDelivery',
            headerName: 'Stock théorique\n(Avant livraison)',
            flex: 1,
            editable: false,
            valueGetter: (params) => params.row.theoricalCount,
        },
        {
            field: 'theoricalCountAfterDelivery',
            headerName: 'Stock théorique\n(Après livraison)',
            flex: 1,
            editable: false,
            valueGetter: (params) => params.row.theoricalCount + (params.row.reapproNeeds || 0),
        },
        {
            field: 'inventoryCount',
            headerName: 'Stock relevé',
            flex: 1,
            editable: false,
            renderCell: (params) => {
                return params.row.inventoryCount + params.row.unscannedCount;
            },
        },
        {
            field: 'delta',
            headerName: 'Delta',
            renderCell: (params) => {
                // Delta already accounts for reappro needs in useInventoryProductRows
                const delta = params.row.delta > 0 ? `+${params.row.delta}` : `${params.row.delta}`;
                return delta;
            },
        },
        ...(showValidationColumn
            ? ([
                  {
                      field: 'validation',
                      headerName: 'Valider',
                      width: 100,
                      renderCell: (params) => {
                          const isProblematic = problematicProducts.includes(params.row.reference);
                          if (!isProblematic) return null;

                          return (
                              <Checkbox
                                  checked={validatedProducts.has(params.row.reference)}
                                  onChange={(e) => {
                                      onValidationChange?.(params.row.reference, e.target.checked);
                                  }}
                                  disabled={inventoryStatus !== 'pending'}
                              />
                          );
                      },
                  },
              ] as GridColDef[])
            : []),
    ];

    const filteredRows = productRows
        .filter((row) => includesLowerCase(row.label, search))
        .sort((a, b) => {
            if (inventoryStatus === 'pending') {
                // Sort by absolute delta in descending order (highest differences first)
                return -b.delta + a.delta;
            }
            return 0; // No sorting for other statuses
        });

    return (
        <Paper
            sx={{
                marginTop: '16px',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', padding: '16px' }}>
                <TextField
                    variant="outlined"
                    label="Recherche"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                    }}
                />
                <div style={{ flexGrow: 1 }} />
            </div>
            <SimpleDataGrid
                columns={stockColumnsDefs}
                rows={filteredRows}
                loading={isLoading}
                checkboxSelection={false}
                pageSizeOptions={[100]}
                getRowClassName={(params) => {
                    // Check if this row has a problematic delta
                    if (problematicProducts.includes(params.row.reference)) {
                        return 'problematic-row';
                    }
                    return '';
                }}
                sx={{
                    '& .MuiDataGrid-columnHeaderTitle': {
                        whiteSpace: 'pre-line',
                        lineHeight: '1.2',
                        textAlign: 'center',
                        overflow: 'visible',
                    },
                    '& .MuiDataGrid-columnHeader': {
                        overflow: 'visible',
                    },
                    '& .problematic-row': {
                        'backgroundColor': '#ffebee !important',
                        '&:hover': {
                            backgroundColor: '#ffcdd2 !important',
                        },
                    },
                }}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 10,
                            page: 0,
                        },
                    },
                }}
            />
        </Paper>
    );
};
